import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { handleError } from "../../utils/errorHandler";
import "./RepDetailsPage.css";
import PageContent from "../shared/PageContent/PageContent";
import EditRepInfoModal from "./EditRepInfoModal";
import RepDocAIExtractModal from "./RepDocAIExtractModal";
import DeactivateRepModal from "./DeactivateRepModal";
import RepDealTab from "./RepDealTab";
import {
  getCachedRepDetails,
  setCachedRepDetails,
} from "../../utils/repDetailsCache";
import {
  Checkbox,
  Modal,
  Stack,
  Group,
  Text,
  PinInput,
  Button,
  Alert,
  Divider,
  Loader,
  Title,
  Select,
  Switch,
} from "@mantine/core";
import {
  IconLock,
  IconShieldCheck,
  IconInfoCircle,
  IconAlertCircle,
  IconMail,
  IconPhone,
  IconMessageCircle,
  IconCalendar,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconUser,
  IconEdit,
  IconCurrencyDollar,
  IconClock,
  IconTrophy,
  IconNetwork,
  IconHeart,
  IconActivity,
  IconTargetArrow,
  IconVideo,
  IconFileText,
  IconUsers,
  IconChecklist,
  IconCloudUpload,
  IconX,
  IconUserOff,
  IconCheck,
  IconReceipt,
} from "@tabler/icons-react";
import "../Organization/OrganizationPage.css";
import {
  DEFAULT_PIN_UNLOCK_DURATION,
  PIN_UNLOCK_DURATIONS,
  clearPinUnlockSession,
  isPinUnlockSessionActive,
  startPinUnlockSession,
} from "../../utils/pinUnlockSession";

// The page chrome: the routed page's header, or — in the drawer — a plain
// scroll container. Defined at module scope on purpose: a component created
// inside the render function gets a new identity every render, which makes
// React remount the whole subtree on each state change (every PIN digit or
// edit-form keystroke would blow away focus and land it on the modal's X).
const RepDetailsShell = ({ asPanel, customHeader, children }) =>
  asPanel ? (
    <div className="rep-details-panel-shell">{children}</div>
  ) : (
    <PageContent customHeader={customHeader}>{children}</PageContent>
  );

// Renders as a full routed page by default, or — when `asPanel` is set — as the
// body of a right-side drawer (opened over a list), with `repId` supplied by the
// opener and `onClose` closing the drawer.
function RepDetailsPage({
  repId: repIdProp,
  asPanel = false,
  onClose,
  // The list row's rep (name, avatar, type, contact) — lets the drawer paint
  // its header instantly while the full profile loads.
  initialRep = null,
} = {}) {
  const { id: routeId } = useParams();
  const id = repIdProp != null ? String(repIdProp) : routeId;
  const navigate = useNavigate();
  // `?tab=` lets other pages (e.g. the add-deal flow) return to a specific tab.
  const [searchParams] = useSearchParams();
  const { user, getVerifiedJWT } = useAuth();
  // Seed from the list row (if the opener handed one over) so the header and
  // tabs render immediately; the full profile fetch then fills in the rest.
  const [rep, setRep] = useState(() =>
    initialRep
      ? {
          ...initialRep,
          first_name: initialRep.first_name ?? "",
          last_name: initialRep.last_name ?? "",
          rep_type_name:
            initialRep.rep_type?.name ??
            initialRep.rep_type_name ??
            initialRep.role_name ??
            null,
          email: initialRep.email ?? null,
          phone_number: initialRep.phone_number ?? initialRep.phone ?? null,
          profile_image_url:
            initialRep.profile_image_url ??
            initialRep.profile_image ??
            initialRep.avatar_url ??
            null,
          groups: initialRep.groups ?? [],
        }
      : null
  );
  const [loading, setLoading] = useState(!initialRep);
  // False until the full profile has loaded at least once (the seeded header
  // shows before that; the body dims until the details arrive).
  const [detailsReady, setDetailsReady] = useState(false);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(
    () =>
      initialRep?.profile_image_url ??
      initialRep?.profile_image ??
      initialRep?.avatar_url ??
      null
  );
  const [reps, setReps] = useState([]);
  const [repTypes, setRepTypes] = useState([]);
  const [recruitingSourceTypes, setRecruitingSourceTypes] = useState([]);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinVerifying, setPinVerifying] = useState(false);
  const [pinError, setPinError] = useState(null);
  const [unlockDuration, setUnlockDuration] = useState(
    String(DEFAULT_PIN_UNLOCK_DURATION)
  );
  const [showCompensation, setShowCompensation] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  // "details", "history", "compensation" or "documents". The deal lives behind
  // the PIN on its own Compensation tab, so ?tab=deal (the add-deal flow's
  // return link) lands there.
  const [activeTab, setActiveTab] = useState(() => {
    // The drawer always opens on Details: the URL's `?tab=` belongs to the page
    // underneath (e.g. the downline page's own tabs), not to this rep.
    if (asPanel) return "details";
    const requested = searchParams.get("tab");
    if (!requested) return "details";
    if (requested === "deal") return "compensation";
    if (requested === "history") return "activity";
    const known = [
      "details",
      "activity",
      "compensation",
      "documents",
      "paystubs",
    ];
    return known.includes(requested) ? requested : "details";
  });
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Documents tab state
  const [repDocuments, setRepDocuments] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  // The uploaded file the AI pay reader is open on (null = closed).
  const [aiExtractFile, setAiExtractFile] = useState(null);
  // Upload-modal opt-in: read the file's pay with AI as soon as it lands.
  const [uploadReadPay, setUploadReadPay] = useState(false);
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [renamingFile, setRenamingFile] = useState(null);
  const [renameLabel, setRenameLabel] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditGroupsModalOpen, setIsEditGroupsModalOpen] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [repGroups, setRepGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);

  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    instagram: "",
    linkedin: "",
    birthday: "",
    saleStartDate: "",
    saleEndDate: "",
    recruitingSource: "",
    isManager: false,
    manager: "",
    recruitedBy: "",
    revenueGoal: "",
  });

  // The rep-info form no longer carries pay fields — pay lives on the deal,
  // behind its own PIN — so entering edit mode needs no PIN.
  // The edit form's dropdown lookups (manager/recruiter roster, rep types,
  // recruiting sources) are no longer part of the profile read — the roster is
  // a full downline walk — so they load on demand the first time Edit opens.
  const loadEditLookups = async () => {
    if (reps.length || repTypes.length || recruitingSourceTypes.length) return;
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint("/v1/rep/edit-lookups"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (!response.ok) return;
      const result = await response.json();
      const data = result?.data ?? {};
      setReps(data.manager_reps || []);
      setRepTypes(data.rep_types || []);
      setRecruitingSourceTypes(data.recruiting_source_types || []);
    } catch (err) {
      // The dropdowns simply stay empty; the rest of the form still works.
    }
  };

  const handleEditClick = () => {
    if (!rep) return;
    loadEditLookups();

    setEditFormData({
      firstName: rep.first_name || "",
      lastName: rep.last_name || "",
      email: rep.email || "",
      phoneNumber: rep.phone_number || "",
      instagram: rep.instagram || "",
      linkedin: rep.linked_in || "",
      birthday: rep.birthday ? rep.birthday.split("T")[0] : "",
      saleStartDate: rep.sale_start_date
        ? rep.sale_start_date.split("T")[0]
        : "",
      saleEndDate: rep.sale_end_date ? rep.sale_end_date.split("T")[0] : "",
      recruitingSource: rep.recruiting_source_id
        ? String(rep.recruiting_source_id)
        : "",
      isManager: rep.is_manager || false,
      manager: rep.manager?.user?.rep_id ? String(rep.manager.user.rep_id) : "",
      recruitedBy: rep.recruiter?.user?.rep_id
        ? String(rep.recruiter.user.rep_id)
        : "",
      revenueGoal: rep.revenue_goal ? rep.revenue_goal.toLocaleString() : "",
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      instagram: "",
      linkedin: "",
      birthday: "",
      saleStartDate: "",
      saleEndDate: "",
      recruitingSource: "",
      isManager: false,
      manager: "",
      recruitedBy: "",
      revenueGoal: "",
    });
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhoneChange = value => {
    const cleaned = value.replace(/\D/g, "");
    let formattedValue = cleaned;

    if (cleaned.length >= 6) {
      formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 3) {
      formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }

    setEditFormData(prev => ({
      ...prev,
      phoneNumber: formattedValue,
    }));
  };

  const handleRevenueGoalChange = value => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, "");

    // Format with commas
    const formatted = cleaned ? parseInt(cleaned).toLocaleString() : "";

    setEditFormData(prev => ({
      ...prev,
      revenueGoal: formatted,
    }));
  };

  const safeParseInt = value => {
    if (!value || value === "") return undefined;
    const parsed = parseInt(value);
    return isNaN(parsed) ? undefined : parsed;
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const cleanPhoneNumber = editFormData.phoneNumber.replace(/\D/g, "");
      const cleanRevenueGoal = editFormData.revenueGoal
        ? editFormData.revenueGoal.replace(/,/g, "").trim()
        : "";

      const parsedRevenueGoal = cleanRevenueGoal
        ? parseFloat(cleanRevenueGoal)
        : null;

      // Validate parsed values
      if (parsedRevenueGoal !== null && isNaN(parsedRevenueGoal)) {
        setError("Invalid revenue goal value. Please enter a valid number.");
        setSaving(false);
        return;
      }

      const apiData = {
        first_name: editFormData.firstName || undefined,
        last_name: editFormData.lastName || undefined,
        email: editFormData.email || undefined,
        phone_number: cleanPhoneNumber || undefined,
        instagram_username: editFormData.instagram || undefined,
        linkedin_profile: editFormData.linkedin || undefined,
        birthday: editFormData.birthday || undefined,
        sale_start_date: editFormData.saleStartDate || undefined,
        sale_end_date: editFormData.saleEndDate || undefined,
        is_manager: editFormData.isManager,
        manager: safeParseInt(editFormData.manager),
        recruited_by: safeParseInt(editFormData.recruitedBy),
        recruiting_source_id: safeParseInt(editFormData.recruitingSource),
        revenue_goal: parsedRevenueGoal,
      };

      const response = await fetch(getApiEndpoint(`/v1/rep/details/${id}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        await fetchRepDetails(true); // Skip loading state to prevent flicker
        setIsEditing(false);
        setError(null);
      } else {
        const data = await response.json();
        setError(
          data.message ||
            JSON.stringify(data) ||
            "Failed to update rep information"
        );
      }
    } catch (err) {
      setError("Failed to update rep information");
    } finally {
      setSaving(false);
    }
  };

  // Fetch basic rep details from API (no PIN required)
  const fetchRepDetails = async (skipLoadingState = false) => {
    try {
      // Only show the full-panel loader when there's nothing to paint yet —
      // a seeded header stays visible while the profile loads behind it.
      if (!skipLoadingState && !rep) {
        setLoading(true);
      }
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Failed to fetch rep details");
        return;
      }

      const applyRepData = data => {
        setRep(data);
        setProfileImageUrl(data.profile_image_url || null);
        setDetailsReady(true);
        setError(null);
      };

      // A recent read (row hover prefetch, or a just-closed drawer) serves
      // instantly; otherwise fetch and cache for the next open.
      const cached = getCachedRepDetails(id);
      if (cached) {
        applyRepData(cached);
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/details/${id}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        setCachedRepDetails(id, data);
        applyRepData(data);
      } else {
        setError("Failed to fetch rep details");
      }
    } catch (err) {
      setError("Failed to fetch rep details");
    } finally {
      setLoading(false);
    }
  };

  // Unlocks the deal behind the PIN. Verified against /v1/user/verify-pin —
  // the same endpoint the downline pay toggle and PinVerificationModal use —
  // and the unlock is shared across the app via the pin unlock session.
  const verifyPin = async (pinValue, openPinModalOnFailure = true) => {
    try {
      setPinVerifying(true);
      setPinError(null);

      if (!pinValue || String(pinValue).length !== 4) {
        setPinError("Please enter a 4-digit PIN");
        return;
      }

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setPinError("Authentication failed");
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/user/verify-pin"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          pin: parseInt(pinValue),
          duration_minutes: Number(unlockDuration),
        }),
      });

      const data = await response.json().catch(() => ({}));

      // A wrong PIN still comes back 200 with success: true — only
      // data.verified says whether it actually matched, and only a match comes
      // with the unlock grant the deal endpoint requires.
      if (
        response.ok &&
        data?.data?.verified === true &&
        data?.data?.pin_token
      ) {
        startPinUnlockSession({
          token: data.data.pin_token,
          expiresIn: data.data.expires_in,
          durationMinutes: Number(unlockDuration),
        });
        setShowCompensation(true);
        setIsPinModalOpen(false);
        setPin("");
        return;
      }

      clearPinUnlockSession();
      setPinError(data.message || "Invalid PIN. Please try again.");
      setPin("");
      if (openPinModalOnFailure) setIsPinModalOpen(true);
    } catch (err) {
      clearPinUnlockSession();
      setPinError("Failed to verify PIN. Please try again.");
      setPin("");
      if (openPinModalOnFailure) setIsPinModalOpen(true);
    } finally {
      setPinVerifying(false);
    }
  };

  const handleRepUpdate = () => {
    fetchRepDetails();
  };

  // An unlock session already covers this rep, so re-entry isn't needed.
  const [savingBackendEligible, setSavingBackendEligible] = useState(false);

  // Flip whether this rep earns backend this season. The API rebuilds the
  // rate stack, so the pay figures elsewhere update on their next load.
  const handleBackendEligibleChange = async eligible => {
    try {
      setSavingBackendEligible(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(
        getApiEndpoint(`/v1/rep/${id}/backend-eligibility`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ backend_eligible: eligible }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || `HTTP ${response.status}`);
      }
      setRep(prev => (prev ? { ...prev, backend_eligible: eligible } : prev));
    } catch (err) {
      handleError(err, "Failed to update backend eligibility");
    } finally {
      setSavingBackendEligible(false);
    }
  };

  const handleViewCompensation = () => {
    if (isPinUnlockSessionActive()) {
      setShowCompensation(true);
      return;
    }

    setIsPinModalOpen(true);
  };

  // The API refused the deal for want of a PIN unlock — the grant lapsed or was
  // never valid. Re-lock and ask again rather than showing an error.
  const handlePinExpired = useCallback(() => {
    clearPinUnlockSession();
    setShowCompensation(false);
    setIsPinModalOpen(true);
  }, []);

  const handlePinComplete = value => {
    setPin(value);
    if (value.length === 4) {
      setTimeout(() => {
        verifyPin(value);
      }, 300);
    }
  };

  const handleClosePinModal = () => {
    setIsPinModalOpen(false);
    setPin("");
    setPinError(null);
    setUnlockDuration(String(DEFAULT_PIN_UNLOCK_DURATION));
  };

  // The unlock is time-boxed; re-lock the deal the moment the session lapses.
  useEffect(() => {
    if (!showCompensation) return undefined;

    const interval = setInterval(() => {
      if (!isPinUnlockSessionActive()) setShowCompensation(false);
    }, 1000);

    return () => clearInterval(interval);
  }, [showCompensation]);

  const getInitials = () => {
    if (rep?.first_name && rep?.last_name) {
      return `${rep.first_name.charAt(0)}${rep.last_name.charAt(0)}`.toUpperCase();
    } else if (rep?.email) {
      return rep.email.charAt(0).toUpperCase();
    }
    return "R";
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatMonthDay = dateString => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate();
    const mod10 = day % 10;
    const mod100 = day % 100;
    const suffix =
      mod10 === 1 && mod100 !== 11
        ? "st"
        : mod10 === 2 && mod100 !== 12
          ? "nd"
          : mod10 === 3 && mod100 !== 13
            ? "rd"
            : "th";
    const month = date.toLocaleDateString("en-US", { month: "long" });
    return `${month} ${day}${suffix}`;
  };

  const formatPhoneNumber = phoneNumber => {
    if (!phoneNumber) return "N/A";
    const cleaned = phoneNumber.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  };

  const formatHistoryDate = value => {
    if (!value) return "Unknown date";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Unknown date";
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getHistoryAccent = source => {
    switch (source) {
      case "recruiting":
        return { bg: "#eff6ff", color: "#2563eb", label: "Recruiting" };
      case "esignature":
        return { bg: "#f5f3ff", color: "#7c3aed", label: "E-Signature" };
      case "team_event":
        return { bg: "#ecfdf5", color: "#059669", label: "Team Event" };
      case "manager_meeting":
        return {
          bg: "#eef2ff",
          color: "#4f46e5",
          label: "Manager Meeting",
        };
      case "attendance":
        return { bg: "#fff7ed", color: "#b9491c", label: "Attendance" };
      case "attendance_event":
        return {
          bg: "#ecfeff",
          color: "#0f766e",
          label: "Attendance Event",
        };
      default:
        return { bg: "#f3f4f6", color: "#4b5563", label: "History" };
    }
  };

  const getHistoryIcon = source => {
    switch (source) {
      case "recruiting":
        return <IconUser size={18} />;
      case "esignature":
        return <IconFileText size={18} />;
      case "team_event":
        return <IconUsers size={18} />;
      case "manager_meeting":
        return <IconCalendar size={18} />;
      case "attendance":
        return <IconChecklist size={18} />;
      case "attendance_event":
        return <IconCalendar size={18} />;
      default:
        return <IconClock size={18} />;
    }
  };

  // Calculate revenue goal progress
  const calculateRevenueProgress = () => {
    const current = rep?.current_revenue || 0;
    const goal = rep?.revenue_goal || 0;
    const remaining = goal > 0 ? goal - current : 0;
    const percentage = goal > 0 ? Math.round((current / goal) * 100) : 0;
    return { current, goal, remaining, percentage };
  };

  const revenue = rep
    ? calculateRevenueProgress()
    : { current: 0, goal: 0, remaining: 0, percentage: 0 };

  // Load history function
  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setIsLoadingHistory(false);
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/${id}/history`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setHistory(result.data || []);
      } else {
        setHistory([]);
      }
    } catch (err) {
      // Failed to load history
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch available groups
  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setLoadingGroups(false);
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/groups/owned"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setAvailableGroups(result.data || []);
        // Set current rep groups
        setRepGroups(rep?.groups?.map(g => g.id) || []);
      }
    } catch (err) {
      // Failed to fetch groups
    } finally {
      setLoadingGroups(false);
    }
  };

  // Handle opening edit groups modal
  const handleOpenEditGroups = () => {
    fetchGroups();
    setIsEditGroupsModalOpen(true);
  };

  // Toggle group membership
  const toggleGroup = groupId => {
    setRepGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // Save group changes
  const handleSaveGroups = async () => {
    setSavingGroups(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/${id}/groups`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          group_ids: repGroups,
        }),
      });

      if (response.ok) {
        await fetchRepDetails();
        setIsEditGroupsModalOpen(false);
      } else {
        const data = await response.json();
        alert(data.message || "Failed to update groups");
      }
    } catch (err) {
      alert("Failed to update groups");
    } finally {
      setSavingGroups(false);
    }
  };

  // Handle delete rep
  const handleDeleteRep = async () => {
    setIsDeleting(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        alert("Authentication failed");
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/details/${id}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        setIsDeleteModalOpen(false);
        if (asPanel && onClose) {
          onClose();
        } else if (user?.role === "COMPANY_ADMIN") {
          navigate("/dashboard");
        } else {
          navigate("/reps");
        }
      } else {
        const data = await response.json();
        alert(data.message || "Failed to delete rep");
      }
    } catch (err) {
      alert("Failed to delete rep");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchRepDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch documents for this rep
  const fetchRepDocuments = async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint(`/v1/rep/${id}/documents`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        setRepDocuments(result.data);
      } else {
        setDocumentsError("Failed to load documents");
      }
    } catch {
      setDocumentsError("Failed to load documents");
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Upload one or more documents for this rep. A single file keeps the custom
  // label + optional AI read; a multi-file drop uploads each under its own
  // filename with no AI pass (matching the iOS behavior).
  const handleUploadDocument = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const single = uploadFiles.length === 1;
      const failed = [];
      let aiTarget = null;
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("label", single ? uploadLabel || file.name : file.name);
        try {
          const response = await fetch(
            getApiEndpoint(`/v1/rep/${id}/documents/upload`),
            {
              method: "POST",
              headers: { Authorization: `Bearer ${jwt}` },
              body: formData,
            }
          );
          if (response.ok) {
            const result = await response.json().catch(() => ({}));
            if (single && uploadReadPay && result?.data?.id) {
              aiTarget = result.data;
            }
          } else {
            failed.push(file.name);
          }
        } catch {
          failed.push(file.name);
        }
      }
      setIsUploadModalOpen(false);
      setUploadFiles([]);
      setUploadLabel("");
      setUploadReadPay(false);
      await fetchRepDocuments();
      if (failed.length > 0) {
        alert(`Some files didn't upload: ${failed.join(", ")}`);
      }
      // Straight into the AI reader when the uploader asked for it, so an
      // offer letter becomes a draft pay deal in one pass.
      if (aiTarget) {
        setAiExtractFile(aiTarget);
      }
    } finally {
      setUploading(false);
    }
  };

  // Rename a manually uploaded attachment
  const handleRenameAttachment = async () => {
    if (!renamingFile || !renameLabel.trim()) return;
    setRenaming(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(
        getApiEndpoint(
          `/v1/rep/${id}/documents/attachments/${renamingFile.id}`
        ),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ label: renameLabel.trim() }),
        }
      );
      if (response.ok) {
        setRenamingFile(null);
        setRenameLabel("");
        await fetchRepDocuments();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.message || "Failed to rename document");
      }
    } catch {
      alert("Failed to rename document");
    } finally {
      setRenaming(false);
    }
  };

  // Delete a manually uploaded attachment
  const handleDeleteAttachment = async attachmentId => {
    if (!window.confirm("Delete this file?")) return;
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(
        getApiEndpoint(`/v1/rep/${id}/documents/attachments/${attachmentId}`),
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      if (response.ok) {
        await fetchRepDocuments();
      }
    } catch {
      // silent
    }
  };

  // History is no longer fetched on mount: the profile loads first, then the
  // other tabs warm in the background (see the detailsReady effect below).

  // Load order: the card paints from the list row, the profile (Details) loads
  // first, then the other tabs warm in the background fastest-first — history
  // (one query), then documents — so they're ready before they're opened.
  // Compensation stays on demand: it sits behind the PIN.
  useEffect(() => {
    if (!detailsReady) return undefined;
    let cancelled = false;
    (async () => {
      if (history.length === 0 && !isLoadingHistory) await loadHistory();
      if (cancelled) return;
      if (!repDocuments && !documentsLoading) await fetchRepDocuments();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsReady]);

  // Fallbacks if a tab is opened before its background load has finished.
  useEffect(() => {
    if (activeTab === "activity" && history.length === 0 && !isLoadingHistory) {
      loadHistory();
    }
    if (activeTab === "documents" && !repDocuments && !documentsLoading) {
      fetchRepDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const DOWNLINE_TABS = [
    {
      id: "overview",
      label: "Overview",
      icon: IconNetwork,
      route: "/downline?tab=overview",
    },
    { id: "rep-details", label: "Rep Details", icon: IconUser, isActive: true },
    {
      id: "rep-health",
      label: "Rep Health",
      icon: IconHeart,
      route: "/downline?tab=rep-health",
    },
    {
      id: "training-stats",
      label: "Training Stats",
      icon: IconActivity,
      route: "/downline?tab=training-stats",
    },
    {
      id: "programs",
      label: "Program Tracking",
      icon: IconTargetArrow,
      route: "/downline?tab=programs",
    },
    {
      id: "events",
      label: "Events",
      icon: IconCalendar,
      route: "/downline?tab=events",
    },
    {
      id: "pitch-reviews",
      label: "Pitch Reviews",
      icon: IconVideo,
      route: "/downline?tab=pitch-reviews",
    },
  ];

  const downlineCustomHeader = (
    <div className="org-page-custom-header">
      <div className="org-page-header-left">
        <h1 className="org-page-main-title">Downline Management</h1>
        <nav className="org-page-nav-tabs" role="tablist">
          {DOWNLINE_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={!!tab.isActive}
                className={`org-page-nav-tab${tab.isActive ? " active" : ""}`}
                onClick={() => {
                  if (tab.isActive) return;
                  navigate(tab.route || "/downline");
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );

  if (loading) {
    return (
      <RepDetailsShell asPanel={asPanel} customHeader={downlineCustomHeader}>
        <div className="loader-container">
          <Loader size="lg" />
          <p>Loading rep details...</p>
        </div>
      </RepDetailsShell>
    );
  }

  if (error && !isEditing) {
    return (
      <RepDetailsShell asPanel={asPanel} customHeader={downlineCustomHeader}>
        <div className="error-container">
          <p
            style={{ color: "#dc2626", fontSize: "18px", marginBottom: "20px" }}
          >
            Error: {error}
          </p>
          <button
            onClick={() => navigate("/reps")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              background: "white",
              cursor: "pointer",
            }}
          >
            Back to Rep Management
          </button>
        </div>
      </RepDetailsShell>
    );
  }

  if (!rep) {
    return (
      <RepDetailsShell asPanel={asPanel} customHeader={downlineCustomHeader}>
        <div className="error-container">
          <p
            style={{ color: "#dc2626", fontSize: "18px", marginBottom: "20px" }}
          >
            Rep not found
          </p>
          <button
            onClick={() => navigate("/reps")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              background: "white",
              cursor: "pointer",
            }}
          >
            Back to Rep Management
          </button>
        </div>
      </RepDetailsShell>
    );
  }

  // Activity history now lives in its own column to the right of the profile
  // (no longer a tab), so it renders alongside whichever tab is active.
  const historyPanel = (
    <div className="rep-details-history-inner">
      {/* Revenue Pacing Section — shown at the top of the history panel. */}
      {rep && (
        <div
          className="revenue-goal-section"
          style={{ padding: "16px 20px", marginBottom: "24px" }}
        >
          <div className="revenue-goal-header">
            <h3 style={{ fontSize: "16px" }}>
              <IconTrophy className="trophy-icon" size={18} />
              Personal Rep Goal
            </h3>
            <span className="completion-text">
              {revenue.percentage}% Complete
            </span>
          </div>
          <div className="revenue-goal-content">
            <div>
              <p className="revenue-current">
                ${revenue.current.toLocaleString()}
              </p>
              {isEditing ? (
                <div style={{ marginTop: "8px" }}>
                  <input
                    type="text"
                    value={editFormData.revenueGoal}
                    onChange={e => handleRevenueGoalChange(e.target.value)}
                    disabled={saving}
                    placeholder="Enter goal amount"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "14px",
                      width: "200px",
                    }}
                  />
                </div>
              ) : (
                <p className="revenue-goal-text">
                  of ${revenue.goal.toLocaleString()} goal
                </p>
              )}
            </div>
            <div className="revenue-remaining">
              <p className="amount">${revenue.remaining.toLocaleString()}</p>
              <p className="label">remaining</p>
            </div>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar"
              style={{ width: `${revenue.percentage}%` }}
            ></div>
          </div>
        </div>
      )}
      <h2 className="rep-details-history-title">Activity History</h2>
      {isLoadingHistory ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Loader size="lg" />
          <p style={{ marginTop: "16px", color: "#6b7280" }}>
            Loading history...
          </p>
        </div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <IconClock
            size={48}
            color="#9ca3af"
            style={{ marginBottom: "16px" }}
          />
          <h3
            style={{
              color: "#6b7280",
              fontSize: "18px",
              marginBottom: "8px",
            }}
          >
            No History Yet
          </h3>
          <p style={{ color: "#9ca3af", fontSize: "14px" }}>
            Activity history will appear here once available
          </p>
        </div>
      ) : (
        <div>
          {history.map((item, index) => (
            <div
              key={item.id || index}
              style={{
                padding: "16px",
                marginBottom: "12px",
                background: "white",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                display: "flex",
                gap: "14px",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: getHistoryAccent(item.source).bg,
                  color: getHistoryAccent(item.source).color,
                  flexShrink: 0,
                }}
              >
                {getHistoryIcon(item.source)}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    flexWrap: "wrap",
                    marginBottom: "6px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#111827",
                        marginBottom: "4px",
                      }}
                    >
                      {item.event_type || "History Event"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "2px 8px",
                          borderRadius: "999px",
                          fontSize: "11px",
                          fontWeight: "700",
                          letterSpacing: "0.02em",
                          background: getHistoryAccent(item.source).bg,
                          color: getHistoryAccent(item.source).color,
                        }}
                      >
                        {getHistoryAccent(item.source).label}
                      </span>
                      {item.timeline_group === "pre-conversion" && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: "700",
                            background: "#fef3c7",
                            color: "#b45309",
                          }}
                        >
                          Before Becoming a Rep
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatHistoryDate(item.event_date || item.created_at)}
                  </div>
                </div>

                {item.metadata?.title &&
                item.metadata.title !== item.notes &&
                item.metadata.title !== item.event_type ? (
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: item.notes ? "6px" : "0",
                    }}
                  >
                    {item.metadata.title}
                  </div>
                ) : null}

                {item.notes ? (
                  <p
                    style={{
                      margin: 0,
                      color: "#4b5563",
                      fontSize: "14px",
                      lineHeight: 1.5,
                    }}
                  >
                    {item.notes}
                  </p>
                ) : null}

                {(item.location || item.status || item.document_title) && (
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      flexWrap: "wrap",
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    {item.location ? (
                      <span>Location: {item.location}</span>
                    ) : null}
                    {item.status ? (
                      <span>
                        Status: {String(item.status).replace(/_/g, " ")}
                      </span>
                    ) : null}
                    {item.document_title ? (
                      <span>Document: {item.document_title}</span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <RepDetailsShell asPanel={asPanel} customHeader={downlineCustomHeader}>
      <div
        className={`rep-details-page-container${asPanel ? " as-panel" : ""}`}
      >
        <div className="rep-details-layout">
          <div className="rep-details-main">
            {/* Profile Header */}
            <div className="rep-details-profile-header">
              {asPanel && (
                <button
                  type="button"
                  className="rep-details-panel-close"
                  onClick={() => {
                    // While editing this doubles as the cancel control.
                    if (isEditing) {
                      setShowCancelConfirmModal(true);
                    } else {
                      onClose?.();
                    }
                  }}
                  aria-label={isEditing ? "Cancel editing" : "Close"}
                >
                  <IconX size={20} />
                </button>
              )}
              <div className="rep-details-header-actions">
                {!isEditing ? (
                  <div className="rep-details-icon-actions">
                    <button
                      type="button"
                      className="rep-details-icon-btn"
                      onClick={handleEditClick}
                      aria-label="Edit rep"
                      title="Edit"
                    >
                      <IconEdit size={18} />
                    </button>
                    <button
                      type="button"
                      className="rep-details-icon-btn"
                      onClick={() => setIsDeactivateModalOpen(true)}
                      aria-label="Deactivate rep"
                      title="Deactivate rep"
                    >
                      <IconUserOff size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="rep-details-icon-actions">
                    <button
                      type="button"
                      className="rep-details-icon-btn"
                      onClick={() => setShowSaveConfirmModal(true)}
                      disabled={saving}
                      aria-label="Save changes"
                      title="Save"
                    >
                      {saving ? (
                        <Loader size="xs" color="white" />
                      ) : (
                        <IconCheck size={18} />
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="rep-details-header-content">
                <div className="rep-details-avatar-wrapper">
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt="Profile"
                      className="rep-details-avatar"
                    />
                  ) : (
                    <div className="rep-details-avatar-placeholder">
                      {getInitials()}
                    </div>
                  )}
                </div>

                <div className="rep-details-profile-info">
                  {isEditing ? (
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="text"
                        value={editFormData.firstName}
                        onChange={e =>
                          handleInputChange("firstName", e.target.value)
                        }
                        disabled={saving}
                        placeholder="First Name"
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "16px",
                          fontWeight: "600",
                        }}
                      />
                      <input
                        type="text"
                        value={editFormData.lastName}
                        onChange={e =>
                          handleInputChange("lastName", e.target.value)
                        }
                        disabled={saving}
                        placeholder="Last Name"
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "16px",
                          fontWeight: "600",
                        }}
                      />
                    </div>
                  ) : (
                    <h1 style={{ color: "white" }}>
                      {rep.first_name} {rep.last_name}
                    </h1>
                  )}
                  <p className="role" style={{ color: "white" }}>
                    {rep.rep_type_name || "Sales Representative"}
                  </p>
                  {/* iOS-style quick contact actions */}
                  {!isEditing && (
                    <div className="rep-details-contact-actions">
                      <a
                        className={`rep-details-contact-action${
                          rep.phone_number ? "" : " disabled"
                        }`}
                        href={
                          rep.phone_number
                            ? `sms:${rep.phone_number}`
                            : undefined
                        }
                        aria-disabled={!rep.phone_number}
                        onClick={e => {
                          if (!rep.phone_number) e.preventDefault();
                        }}
                      >
                        <span className="rep-details-contact-icon">
                          <IconMessageCircle size={22} />
                        </span>
                        <span className="rep-details-contact-label">
                          message
                        </span>
                      </a>
                      <a
                        className={`rep-details-contact-action${
                          rep.phone_number ? "" : " disabled"
                        }`}
                        href={
                          rep.phone_number
                            ? `tel:${rep.phone_number}`
                            : undefined
                        }
                        aria-disabled={!rep.phone_number}
                        onClick={e => {
                          if (!rep.phone_number) e.preventDefault();
                        }}
                      >
                        <span className="rep-details-contact-icon">
                          <IconPhone size={22} />
                        </span>
                        <span className="rep-details-contact-label">call</span>
                      </a>
                      <a
                        className={`rep-details-contact-action${
                          rep.email ? "" : " disabled"
                        }`}
                        href={rep.email ? `mailto:${rep.email}` : undefined}
                        aria-disabled={!rep.email}
                        onClick={e => {
                          if (!rep.email) e.preventDefault();
                        }}
                      >
                        <span className="rep-details-contact-icon">
                          <IconMail size={22} />
                        </span>
                        <span className="rep-details-contact-label">mail</span>
                      </a>
                      <a
                        className={`rep-details-contact-action${
                          rep.email ? "" : " disabled"
                        }`}
                        href={
                          rep.email
                            ? `https://calendar.google.com/calendar/render?action=TEMPLATE&add=${encodeURIComponent(
                                rep.email
                              )}`
                            : undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-disabled={!rep.email}
                        onClick={e => {
                          if (!rep.email) e.preventDefault();
                        }}
                      >
                        <span className="rep-details-contact-icon">
                          <IconCalendar size={22} />
                        </span>
                        <span className="rep-details-contact-label">
                          meeting
                        </span>
                      </a>
                    </div>
                  )}
                  {/* Groups moved to their own section at the top of Details. */}
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                padding: "0 12px",
                marginBottom: "12px",
                borderBottom: "2px solid #e5e7eb",
                overflowX: "auto",
              }}
            >
              {[
                { id: "details", label: "Details" },
                { id: "activity", label: "Activity" },
                { id: "compensation", label: "Compensation" },
                { id: "documents", label: "Documents" },
                { id: "paystubs", label: "Paystubs" },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    padding: "12px 6px",
                    whiteSpace: "nowrap",
                    background: "transparent",
                    border: "none",
                    borderBottom:
                      activeTab === t.id
                        ? "3px solid #d0521f"
                        : "3px solid transparent",
                    color: activeTab === t.id ? "#d0521f" : "#1f2937",
                    fontSize: "14px",
                    fontWeight: activeTab === t.id ? "700" : "600",
                    cursor: "pointer",
                    marginBottom: "-2px",
                    transition: "all 0.2s",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Unified Profile Information Section */}
            {activeTab === "details" && (
              <div
                className="rep-details-info-section"
                // Dim the body until the full profile lands behind the seeded
                // header, so half-filled fields don't read as final.
                style={{
                  opacity: detailsReady ? 1 : 0.6,
                  transition: "opacity 0.2s",
                }}
              >
                {!detailsReady && (
                  <div
                    className="loader-container"
                    style={{ minHeight: "120px", padding: "16px 0 24px" }}
                  >
                    <Loader size="md" />
                    <p style={{ margin: 0, color: "#6b7280" }}>
                      Loading details...
                    </p>
                  </div>
                )}
                {/* Error Display */}
                {error && isEditing && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Error Saving Changes"
                    color="red"
                    variant="light"
                    style={{ marginBottom: "24px" }}
                    withCloseButton
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                )}

                {/* Groups — the rep's memberships, as a section at the top of
                    Details (styled like the other sections) rather than pills
                    in the header. */}
                <div style={{ marginBottom: "32px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}
                  >
                    <h2>
                      <IconUsers size={18} />
                      Groups
                    </h2>
                    <Button
                      variant="subtle"
                      size="xs"
                      leftSection={<IconEdit size={14} />}
                      onClick={handleOpenEditGroups}
                    >
                      Edit Groups
                    </Button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginTop: "12px",
                    }}
                  >
                    {rep.groups && rep.groups.length > 0 ? (
                      rep.groups.map(group => (
                        <span
                          key={group.id}
                          title={`Created by ${group.manager_name || "Unknown"}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "6px 12px 6px 8px",
                            borderRadius: "999px",
                            background: "#fff4ee",
                            border: "1px solid #f5c9b3",
                            color: "#b9491c",
                            fontSize: "13px",
                            fontWeight: "600",
                            lineHeight: 1.2,
                          }}
                        >
                          {group.rep_type && group.rep_type.image ? (
                            <img
                              src={group.rep_type.image}
                              alt={group.rep_type.name}
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "#d0521f",
                                color: "white",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <IconUsers size={12} />
                            </span>
                          )}
                          {group.name}
                        </span>
                      ))
                    ) : (
                      <Text size="sm" c="dimmed">
                        Not a member of any groups yet.
                      </Text>
                    )}
                  </div>
                </div>

                {/* Contact Information & Personal Details Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: "48px",
                    marginBottom: "40px",
                  }}
                >
                  {/* Contact Information Column */}
                  <div>
                    <h2 style={{ marginBottom: "24px" }}>
                      <IconUser />
                      Contact Information
                    </h2>
                    <div className="rep-details-info-grid single-column">
                      <div className="rep-details-info-item">
                        <div className="rep-details-info-icon email">
                          <IconMail />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">EMAIL</p>
                          {isEditing ? (
                            <input
                              type="email"
                              value={editFormData.email}
                              onChange={e =>
                                handleInputChange("email", e.target.value)
                              }
                              disabled={saving}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                marginTop: "4px",
                              }}
                            />
                          ) : (
                            <p className="rep-details-info-value">
                              {rep.email ? (
                                <a href={`mailto:${rep.email}`}>{rep.email}</a>
                              ) : (
                                "N/A"
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="rep-details-info-item">
                        <div className="rep-details-info-icon phone">
                          <IconPhone />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">PHONE</p>
                          {isEditing ? (
                            <input
                              type="tel"
                              value={editFormData.phoneNumber}
                              onChange={e => handlePhoneChange(e.target.value)}
                              disabled={saving}
                              placeholder="(555) 123-4567"
                              maxLength={14}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                marginTop: "4px",
                              }}
                            />
                          ) : (
                            <p className="rep-details-info-value">
                              {formatPhoneNumber(rep.phone_number)}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="rep-details-info-item">
                        <div className="rep-details-info-icon instagram">
                          <IconBrandInstagram />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">INSTAGRAM</p>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editFormData.instagram}
                              onChange={e =>
                                handleInputChange("instagram", e.target.value)
                              }
                              disabled={saving}
                              placeholder="@username or URL"
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                marginTop: "4px",
                              }}
                            />
                          ) : (
                            <p className="rep-details-info-value">
                              {rep.instagram ? (
                                rep.instagram.startsWith("http") ||
                                rep.instagram.startsWith("@") ||
                                rep.instagram.includes(".com") ? (
                                  <a
                                    href={
                                      rep.instagram.startsWith("http")
                                        ? rep.instagram
                                        : rep.instagram.startsWith("@")
                                          ? `https://instagram.com/${rep.instagram.replace("@", "")}`
                                          : `https://${rep.instagram}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {rep.instagram}
                                  </a>
                                ) : (
                                  rep.instagram
                                )
                              ) : (
                                "N/A"
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="rep-details-info-item">
                        <div
                          className="rep-details-info-icon"
                          style={{ background: "#e0e7ff", color: "#0077b5" }}
                        >
                          <IconBrandLinkedin />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">LINKEDIN</p>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editFormData.linkedin}
                              onChange={e =>
                                handleInputChange("linkedin", e.target.value)
                              }
                              disabled={saving}
                              placeholder="LinkedIn profile URL"
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                marginTop: "4px",
                              }}
                            />
                          ) : (
                            <p className="rep-details-info-value">
                              {rep.linked_in ? (
                                rep.linked_in.startsWith("http") ||
                                rep.linked_in.includes(".com") ? (
                                  <a
                                    href={
                                      rep.linked_in.startsWith("http")
                                        ? rep.linked_in
                                        : `https://${rep.linked_in}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {rep.linked_in}
                                  </a>
                                ) : (
                                  rep.linked_in
                                )
                              ) : (
                                "N/A"
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Personal Details Column */}
                  <div>
                    <h2 style={{ marginBottom: "24px" }}>
                      <IconUser />
                      Personal Details
                    </h2>
                    <div className="rep-details-info-grid single-column">
                      <div className="rep-details-info-item">
                        <div className="rep-details-info-icon birthday">
                          <IconCalendar />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">BIRTHDAY</p>
                          {isEditing ? (
                            <input
                              type="date"
                              value={editFormData.birthday}
                              onChange={e =>
                                handleInputChange("birthday", e.target.value)
                              }
                              disabled={saving}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                marginTop: "4px",
                              }}
                            />
                          ) : (
                            <p className="rep-details-info-value">
                              {formatDate(rep.birthday)}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="rep-details-info-item">
                        <div
                          className="rep-details-info-icon"
                          style={{
                            background: editFormData.isManager
                              ? "#dcfce7"
                              : "#fee2e2",
                            color: editFormData.isManager
                              ? "#16a34a"
                              : "#dc2626",
                          }}
                        >
                          <IconUser />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">
                            HAS MANAGER PRIVILEGES?
                          </p>
                          {isEditing ? (
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                background: "#f3f4f6",
                                padding: "2px",
                                borderRadius: "6px",
                                marginTop: "4px",
                                width: "fit-content",
                              }}
                            >
                              <button
                                onClick={() =>
                                  handleInputChange("isManager", false)
                                }
                                disabled={saving}
                                style={{
                                  padding: "4px 16px",
                                  background: !editFormData.isManager
                                    ? "white"
                                    : "transparent",
                                  border: "none",
                                  borderRadius: "4px",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  color: !editFormData.isManager
                                    ? "#dc2626"
                                    : "#6b7280",
                                  boxShadow: !editFormData.isManager
                                    ? "0 1px 3px rgba(0,0,0,0.1)"
                                    : "none",
                                }}
                              >
                                No
                              </button>
                              <button
                                onClick={() =>
                                  handleInputChange("isManager", true)
                                }
                                disabled={saving}
                                style={{
                                  padding: "4px 16px",
                                  background: editFormData.isManager
                                    ? "white"
                                    : "transparent",
                                  border: "none",
                                  borderRadius: "4px",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  color: editFormData.isManager
                                    ? "#16a34a"
                                    : "#6b7280",
                                  boxShadow: editFormData.isManager
                                    ? "0 1px 3px rgba(0,0,0,0.1)"
                                    : "none",
                                }}
                              >
                                Yes
                              </button>
                            </div>
                          ) : (
                            <p
                              className="rep-details-info-value"
                              style={{
                                color: rep.is_manager ? "#16a34a" : "#dc2626",
                                fontWeight: "600",
                              }}
                            >
                              {rep.is_manager ? "Yes" : "No"}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="rep-details-info-item">
                        <div className="rep-details-info-icon manager">
                          <IconUser />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">MANAGER</p>
                          {isEditing ? (
                            <select
                              value={editFormData.manager}
                              onChange={e =>
                                handleInputChange("manager", e.target.value)
                              }
                              disabled={saving}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                marginTop: "4px",
                                backgroundColor: "white",
                                cursor: "pointer",
                              }}
                            >
                              <option value="">Select manager...</option>
                              {reps
                                .filter(r => r.is_manager)
                                .map(r => (
                                  <option key={r.rep_id} value={r.rep_id}>
                                    {r.first_name} {r.last_name}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <p className="rep-details-info-value">
                              {rep.manager_name || "N/A"}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="rep-details-info-item">
                        <div
                          className="rep-details-info-icon"
                          style={{ background: "#dbeafe", color: "#2563eb" }}
                        >
                          <IconUser />
                        </div>
                        <div className="rep-details-info-content">
                          <p className="rep-details-info-label">RECRUITED BY</p>
                          {isEditing ? (
                            <select
                              value={editFormData.recruitedBy}
                              onChange={e =>
                                handleInputChange("recruitedBy", e.target.value)
                              }
                              disabled={saving}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "14px",
                                marginTop: "4px",
                                backgroundColor: "white",
                                cursor: "pointer",
                              }}
                            >
                              <option value="">Select recruiter...</option>
                              {reps.map(r => (
                                <option key={r.rep_id} value={r.rep_id}>
                                  {r.first_name} {r.last_name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className="rep-details-info-value">
                              {rep.recruited_by_name || "N/A"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Employment Details Section */}
                <h2 style={{ marginBottom: "24px" }}>
                  <IconClock />
                  Employment Details
                </h2>
                <div
                  className="rep-details-info-grid three-columns"
                  style={{ marginBottom: "40px" }}
                >
                  <div className="rep-details-info-item">
                    <div
                      className="rep-details-info-icon"
                      style={{ background: "#fef3c7", color: "#f59e0b" }}
                    >
                      <IconUser />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">
                        RECRUITING SOURCE
                      </p>
                      {isEditing ? (
                        <select
                          value={editFormData.recruitingSource}
                          onChange={e =>
                            handleInputChange(
                              "recruitingSource",
                              e.target.value
                            )
                          }
                          disabled={saving}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                            backgroundColor: "white",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select recruiting source...</option>
                          {recruitingSourceTypes.map(source => (
                            <option key={source.id} value={source.id}>
                              {source.source_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="rep-details-info-value">
                          {rep.recruiting_source_name || "N/A"}
                        </p>
                      )}
                    </div>
                  </div>

                  {rep.recruiting_source_notes && (
                    <div className="rep-details-info-item">
                      <div
                        className="rep-details-info-icon"
                        style={{ background: "#fef3c7", color: "#f59e0b" }}
                      >
                        <IconUser />
                      </div>
                      <div className="rep-details-info-content">
                        <p className="rep-details-info-label">
                          RECRUITING SOURCE NOTES
                        </p>
                        <p className="rep-details-info-value">
                          {rep.recruiting_source_notes}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="rep-details-info-item">
                    <div className="rep-details-info-icon start-date">
                      <IconCalendar />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">START DATE</p>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editFormData.saleStartDate}
                          onChange={e =>
                            handleInputChange("saleStartDate", e.target.value)
                          }
                          disabled={saving}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        />
                      ) : (
                        <p
                          className="rep-details-info-value"
                          style={{ fontSize: "14px" }}
                        >
                          {formatMonthDay(rep.sale_start_date)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rep-details-info-item">
                    <div className="rep-details-info-icon end-date">
                      <IconCalendar />
                    </div>
                    <div className="rep-details-info-content">
                      <p className="rep-details-info-label">END DATE</p>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editFormData.saleEndDate}
                          onChange={e =>
                            handleInputChange("saleEndDate", e.target.value)
                          }
                          disabled={saving}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        />
                      ) : (
                        <p className="rep-details-info-value">
                          {formatDate(rep.sale_end_date)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                {/* Notes Section */}
                {rep.notes && (
                  <>
                    <h2 style={{ marginBottom: "24px", marginTop: "40px" }}>
                      <IconUser />
                      Notes
                    </h2>
                    <div className="rep-details-notes-content">
                      <p>{rep.notes}</p>
                    </div>
                  </>
                )}

                {/* Bottom Save/Cancel Buttons in Edit Mode */}
                {isEditing && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "12px",
                      marginTop: "40px",
                      paddingTop: "24px",
                      borderTop: "1px solid #e5e7eb",
                    }}
                  >
                    <button
                      onClick={() => setShowCancelConfirmModal(true)}
                      disabled={saving}
                      style={{
                        padding: "12px 32px",
                        background: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "15px",
                        fontWeight: "500",
                        cursor: "pointer",
                        color: "#6b7280",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowSaveConfirmModal(true)}
                      disabled={saving}
                      style={{
                        padding: "12px 32px",
                        background: "#d0521f",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "15px",
                        fontWeight: "500",
                        cursor: "pointer",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {saving ? <Loader size="sm" color="white" /> : null}
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Activity Section — the rep goal + activity history. */}
            {activeTab === "activity" && (
              <div
                className="rep-details-info-section"
                style={{ padding: "24px" }}
              >
                {historyPanel}
              </div>
            )}
            {/* Compensation Section — the rep's live deal, behind the PIN.
            The deal (ps_selling_year_deal) is what the pay engine reads, so it
            is the only pay data shown; the add-deal flow returns here via
            ?tab=deal. */}
            {activeTab === "compensation" && (
              <div className="rep-details-info-section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                    marginBottom: "24px",
                  }}
                >
                  <h2 style={{ margin: 0 }}>
                    <IconCurrencyDollar />
                    Compensation Details
                  </h2>
                  <Switch
                    label="Backend eligible"
                    description={
                      rep?.backend_eligible === false
                        ? "Earns no production bonus; revenue rolls up in full"
                        : "Earns production bonus on their own revenue"
                    }
                    checked={rep?.backend_eligible !== false}
                    disabled={savingBackendEligible}
                    onChange={e =>
                      handleBackendEligibleChange(e.currentTarget.checked)
                    }
                  />
                </div>
                {!showCompensation ? (
                  <div className="rep-details-compensation-empty">
                    <p style={{ marginBottom: "1.5rem", maxWidth: "500px" }}>
                      Compensation information is protected for security. Click
                      below to view this rep's deal — commission, override and
                      marketing-deal scales, upfront pay, bonuses and expenses.
                    </p>
                    <button
                      className="btn btn-lg btn-primary"
                      onClick={handleViewCompensation}
                      style={{ padding: "12px 32px", fontSize: "16px" }}
                    >
                      <IconLock size={20} style={{ marginRight: "8px" }} /> View
                      Compensation Details
                    </button>
                  </div>
                ) : (
                  <>
                    <RepDealTab
                      repId={id}
                      repName={
                        rep
                          ? `${rep.first_name || ""} ${rep.last_name || ""}`.trim()
                          : ""
                      }
                      onPinRequired={handlePinExpired}
                      onUploadDocument={() => {
                        // Open the document uploader with "read pay" pre-checked so
                        // a signed offer/deal doc becomes a draft pay deal.
                        setUploadReadPay(true);
                        setIsUploadModalOpen(true);
                      }}
                    />
                  </>
                )}
              </div>
            )}

            {/* Documents Section */}
            {activeTab === "documents" && (
              <div
                className="rep-details-info-section"
                style={{ padding: "24px" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <h2 style={{ margin: 0 }}>Documents</h2>
                  <button
                    onClick={() => setIsUploadModalOpen(true)}
                    style={{
                      padding: "10px 20px",
                      background: "#d0521f",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    + Upload Document
                  </button>
                </div>

                {documentsLoading && (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <Loader size="lg" />
                    <p style={{ marginTop: "16px", color: "#6b7280" }}>
                      Loading documents...
                    </p>
                  </div>
                )}

                {documentsError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                    mb="md"
                  >
                    {documentsError}
                  </Alert>
                )}

                {!documentsLoading && repDocuments && (
                  <>
                    {/* E-Signed Documents */}
                    <div style={{ marginBottom: "32px" }}>
                      <h3
                        style={{
                          fontSize: "15px",
                          fontWeight: "600",
                          marginBottom: "12px",
                          color: "#374151",
                        }}
                      >
                        System Documents
                      </h3>
                      {repDocuments.signed_documents.length === 0 ? (
                        <p
                          style={{
                            color: "#9ca3af",
                            fontSize: "14px",
                            fontStyle: "italic",
                          }}
                        >
                          No signed documents yet.
                        </p>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {repDocuments.signed_documents.map(doc => (
                            <div
                              key={doc.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "14px 16px",
                                background: "white",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    background: "#fee2e2",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#dc2626",
                                  }}
                                >
                                  PDF
                                </div>
                                <div>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontWeight: "600",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {doc.document_name || "Untitled Document"}
                                  </p>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: "12px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    {new Date(
                                      doc.created_at
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}
                                    {" · "}
                                    <span
                                      style={{
                                        display: "inline-block",
                                        padding: "1px 8px",
                                        borderRadius: "12px",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        background:
                                          doc.status === "completed"
                                            ? "#d1fae5"
                                            : doc.status === "voided"
                                              ? "#fee2e2"
                                              : "#fef3c7",
                                        color:
                                          doc.status === "completed"
                                            ? "#065f46"
                                            : doc.status === "voided"
                                              ? "#991b1b"
                                              : "#92400e",
                                      }}
                                    >
                                      {doc.status
                                        ?.replace(/_/g, " ")
                                        .toUpperCase()}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              {doc.completed_pdf_url && (
                                <a
                                  href={doc.completed_pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    padding: "6px 14px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    color: "#374151",
                                    textDecoration: "none",
                                    background: "white",
                                  }}
                                >
                                  View
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Uploaded Files */}
                    <div>
                      <h3
                        style={{
                          fontSize: "15px",
                          fontWeight: "600",
                          marginBottom: "12px",
                          color: "#374151",
                        }}
                      >
                        Uploaded Files
                      </h3>
                      {repDocuments.uploaded_files.length === 0 ? (
                        <p
                          style={{
                            color: "#9ca3af",
                            fontSize: "14px",
                            fontStyle: "italic",
                          }}
                        >
                          No uploaded files yet. Click "Upload Document" to add
                          files.
                        </p>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {repDocuments.uploaded_files.map(file => (
                            <div
                              key={file.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "14px 16px",
                                background: "white",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    background: "#ede9fe",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    color: "#7c3aed",
                                  }}
                                >
                                  {(file.file_name?.split(".").pop() || "PDF")
                                    .slice(0, 4)
                                    .toUpperCase()}
                                </div>
                                <div>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontWeight: "600",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {file.label}
                                  </p>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: "12px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    {new Date(
                                      file.created_at
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}
                                    {file.file_size
                                      ? ` · ${(file.file_size / 1024).toFixed(0)} KB`
                                      : ""}
                                  </p>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: "8px" }}>
                                {/* Offer letters and comp agreements can be read by
                                AI into a draft pay deal for this rep. */}
                                <button
                                  onClick={() => setAiExtractFile(file)}
                                  style={{
                                    padding: "6px 14px",
                                    border: "1px solid #d0521f",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    color: "#d0521f",
                                    background: "white",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  Read pay with AI
                                </button>
                                <a
                                  href={file.s3_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    padding: "6px 14px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    color: "#374151",
                                    textDecoration: "none",
                                    background: "white",
                                  }}
                                >
                                  View
                                </a>
                                <button
                                  onClick={() => {
                                    setRenamingFile(file);
                                    setRenameLabel(file.label || "");
                                  }}
                                  style={{
                                    padding: "6px 14px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    color: "#374151",
                                    background: "white",
                                    cursor: "pointer",
                                  }}
                                >
                                  Rename
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteAttachment(file.id)
                                  }
                                  style={{
                                    padding: "6px 14px",
                                    border: "1px solid #fca5a5",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    color: "#dc2626",
                                    background: "white",
                                    cursor: "pointer",
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Paystubs Section — placeholder until paystub data is wired up. */}
            {activeTab === "paystubs" && (
              <div
                className="rep-details-info-section"
                style={{ padding: "24px" }}
              >
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <IconReceipt
                    size={48}
                    color="#9ca3af"
                    style={{ marginBottom: "16px" }}
                  />
                  <h3
                    style={{
                      color: "#6b7280",
                      fontSize: "18px",
                      marginBottom: "8px",
                    }}
                  >
                    No Paystubs Yet
                  </h3>
                  <p style={{ color: "#9ca3af", fontSize: "14px" }}>
                    Paystubs will appear here once available
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Upload Document Modal */}
        <Modal
          opened={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setUploadFiles([]);
            setUploadLabel("");
          }}
          title="Upload Documents"
          centered
          size="md"
        >
          <Stack gap="md">
            {/* The upload box: click or drop a file. Naming comes after. */}
            <label
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                if (uploading) return;
                const dropped = Array.from(e.dataTransfer.files || []);
                if (dropped.length) setUploadFiles(dropped);
              }}
              style={{
                display: "block",
                border: "2px dashed #e5e7eb",
                borderRadius: "10px",
                padding: "28px 16px",
                textAlign: "center",
                cursor: uploading ? "default" : "pointer",
                background: "#fafafa",
              }}
            >
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.zip,application/pdf"
                multiple
                disabled={uploading}
                onChange={e => setUploadFiles(Array.from(e.target.files || []))}
                style={{ display: "none" }}
              />
              <Stack gap={6} align="center">
                <IconCloudUpload size={32} color="#d0521f" />
                {uploadFiles.length === 0 ? (
                  <>
                    <Text size="sm" fw={600}>
                      Click to choose or drop a file here
                    </Text>
                    <Text size="xs" c="dimmed">
                      PDF, image, or zip
                    </Text>
                  </>
                ) : uploadFiles.length === 1 ? (
                  <>
                    <Text size="sm" fw={600}>
                      {uploadFiles[0].name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {(uploadFiles[0].size / 1024).toFixed(0)} KB · click to
                      change
                    </Text>
                  </>
                ) : (
                  <>
                    <Text size="sm" fw={600}>
                      {uploadFiles.length} files selected
                    </Text>
                    <Text size="xs" c="dimmed">
                      Each uploads under its own filename · click to change
                    </Text>
                  </>
                )}
              </Stack>
            </label>

            {/* Naming and the AI option only appear once a single file is
                chosen — name it after. */}
            {uploadFiles.length === 1 && (
              <div>
                <Text size="sm" fw={600} mb={4}>
                  Document Name / Label (optional)
                </Text>
                <input
                  type="text"
                  value={uploadLabel}
                  onChange={e => setUploadLabel(e.target.value)}
                  placeholder="Defaults to the file name"
                  disabled={uploading}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            )}
            {uploadFiles.length === 1 && (
              <Checkbox
                checked={uploadReadPay}
                onChange={e => setUploadReadPay(e.currentTarget.checked)}
                disabled={uploading}
                label="Read the pay from this document with AI"
                description="For offer letters and comp agreements. Nothing is saved to the deal until you review the draft."
                color="orange"
              />
            )}
            <Group justify="flex-end" gap="sm" pt="md">
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setUploadFiles([]);
                  setUploadLabel("");
                }}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadDocument}
                loading={uploading}
                disabled={uploadFiles.length === 0 || uploading}
                style={{ background: "#d0521f" }}
              >
                {uploadFiles.length > 1
                  ? `Upload ${uploadFiles.length} files`
                  : "Upload"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Rename Document Modal */}
        <Modal
          opened={renamingFile != null}
          onClose={() => {
            setRenamingFile(null);
            setRenameLabel("");
          }}
          title="Rename Document"
          centered
          size="sm"
        >
          <Stack gap="md">
            <input
              type="text"
              value={renameLabel}
              onChange={e => setRenameLabel(e.target.value)}
              placeholder="Document name"
              disabled={renaming}
              autoFocus
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => {
                  setRenamingFile(null);
                  setRenameLabel("");
                }}
                disabled={renaming}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenameAttachment}
                loading={renaming}
                disabled={!renameLabel.trim() || renaming}
                style={{ background: "#d0521f" }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Modal>

        <RepDocAIExtractModal
          opened={aiExtractFile != null}
          onClose={() => setAiExtractFile(null)}
          repId={id}
          attachment={aiExtractFile}
        />

        <EditRepInfoModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          rep={rep}
          reps={reps}
          repTypes={repTypes}
          recruitingSourceTypes={recruitingSourceTypes}
          onUpdate={handleRepUpdate}
        />

        <DeactivateRepModal
          isOpen={isDeactivateModalOpen}
          onClose={() => setIsDeactivateModalOpen(false)}
          rep={rep}
          onConfirmDeactivate={() => {
            setIsDeactivateModalOpen(false);
            if (asPanel && onClose) {
              onClose();
            } else if (user?.role === "COMPANY_ADMIN") {
              navigate("/dashboard");
            } else {
              navigate("/reps");
            }
          }}
        />

        <Modal
          opened={isPinModalOpen}
          onClose={handleClosePinModal}
          title={
            <Group gap="xs">
              <IconLock size={20} />
              <Title order={3} fw={600}>
                Verify Security PIN
              </Title>
            </Group>
          }
          size="md"
          padding="xl"
          closeOnClickOutside={true}
          closeOnEscape={true}
        >
          <Stack gap="md">
            {pinError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
                variant="light"
              >
                {pinError}
              </Alert>
            )}

            <Alert
              icon={<IconInfoCircle size={16} />}
              title="Secure Information"
              color="blue"
              variant="light"
            >
              Compensation details are protected. Please enter your 4-digit
              security PIN to view this rep's deal — commission, override and
              marketing-deal scales, upfront pay, bonuses and expenses.
            </Alert>

            <Divider />

            <Stack gap="md" align="center">
              <Group gap="xs">
                <IconShieldCheck size={20} />
                <Text fw={600} size="sm">
                  Enter Your PIN
                </Text>
              </Group>
              <PinInput
                length={4}
                value={pin}
                onChange={setPin}
                onComplete={handlePinComplete}
                placeholder="○"
                type="number"
                size="xl"
                disabled={pinVerifying}
                mask
                styles={{
                  input: {
                    borderColor: pinError ? "#fa5252" : undefined,
                  },
                }}
              />
              {pinVerifying && (
                <Group gap="xs">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Verifying PIN...
                  </Text>
                </Group>
              )}
            </Stack>

            <Select
              label="Keep unlocked for"
              value={unlockDuration}
              onChange={value =>
                setUnlockDuration(value || String(DEFAULT_PIN_UNLOCK_DURATION))
              }
              data={PIN_UNLOCK_DURATIONS.map(option => ({
                value: String(option.value),
                label: option.label,
              }))}
              allowDeselect={false}
            />

            <Group justify="space-between" gap="sm" pt="md">
              <Button
                variant="outline"
                onClick={handleClosePinModal}
                disabled={pinVerifying}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={() => verifyPin(pin)}
                loading={pinVerifying}
                leftSection={
                  pinVerifying ? undefined : <IconShieldCheck size={16} />
                }
                disabled={pin.length !== 4}
                size="sm"
              >
                Verify PIN
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Save Confirmation Modal */}
        <Modal
          opened={showSaveConfirmModal}
          onClose={() => setShowSaveConfirmModal(false)}
          title="Confirm Save"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to save these changes to the rep profile?
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setShowSaveConfirmModal(false)}
              >
                No, Go Back
              </Button>
              <Button
                style={{ background: "#d0521f" }}
                onClick={async () => {
                  setShowSaveConfirmModal(false);
                  await handleSaveEdit();
                }}
              >
                Yes, Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Cancel Confirmation Modal */}
        <Modal
          opened={showCancelConfirmModal}
          onClose={() => setShowCancelConfirmModal(false)}
          title="Confirm Cancel"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to cancel? All unsaved changes will be lost.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirmModal(false)}
              >
                No, Keep Editing
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setShowCancelConfirmModal(false);
                  handleCancelEdit();
                }}
              >
                Yes, Discard Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit Groups Modal */}
        <Modal
          opened={isEditGroupsModalOpen}
          onClose={() => setIsEditGroupsModalOpen(false)}
          title="Edit Groups"
          centered
          size="md"
        >
          <style>
            {`
              .orange-checkbox {
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                width: 18px;
                height: 18px;
                border: 2px solid #d1d5db;
                border-radius: 4px;
                cursor: pointer;
                position: relative;
                margin-right: 12px;
                transition: all 0.2s;
              }
              .orange-checkbox:checked {
                background-color: #d0521f;
                border-color: #d0521f;
              }
              .orange-checkbox:checked::after {
                content: '✓';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 14px;
                font-weight: bold;
              }
            `}
          </style>
          <Stack gap="md">
            {loadingGroups ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Loader size="md" />
                <Text size="sm" c="dimmed" mt="md">
                  Loading groups...
                </Text>
              </div>
            ) : availableGroups.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No groups available
              </Text>
            ) : (
              <>
                <Text size="sm" c="dimmed">
                  Select groups to add or remove {rep?.first_name}{" "}
                  {rep?.last_name}:
                </Text>
                <Stack gap="xs">
                  {availableGroups.map(group => (
                    <div
                      key={group.id}
                      onClick={() => toggleGroup(group.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: repGroups.includes(group.id)
                          ? "#fff7ed"
                          : "white",
                        borderColor: repGroups.includes(group.id)
                          ? "#d0521f"
                          : "#e5e7eb",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="orange-checkbox"
                        checked={repGroups.includes(group.id)}
                        onChange={() => toggleGroup(group.id)}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {group.rep_type?.image && (
                            <img
                              src={group.rep_type.image}
                              alt={group.rep_type.name}
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          )}
                          <Text fw={500} size="sm">
                            {group.name}
                          </Text>
                        </div>
                        {group.manager_name && (
                          <Text size="xs" c="dimmed" mt={4}>
                            Created by {group.manager_name}
                          </Text>
                        )}
                      </div>
                    </div>
                  ))}
                </Stack>
              </>
            )}
            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="outline"
                onClick={() => setIsEditGroupsModalOpen(false)}
                disabled={savingGroups}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveGroups}
                loading={savingGroups}
                style={{ background: "#d0521f" }}
              >
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Rep Permanently?"
          centered
          size="md"
        >
          <Stack gap="md">
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Warning: This Action Cannot Be Undone"
              color="red"
              variant="light"
            >
              Deleting this rep will permanently remove all their data from the
              system, including their profile, commission history, training
              records, and all associated information.
            </Alert>
            <Text size="sm">You are about to permanently delete:</Text>
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Text fw={600} size="md" mb="xs">
                {rep?.first_name} {rep?.last_name}
              </Text>
              <Text size="sm" c="dimmed">
                {rep?.email}
              </Text>
              <Text size="sm" c="dimmed">
                Rep ID: {rep?.rep_id}
              </Text>
            </div>
            <Text size="sm" c="dimmed">
              Consider using the "Deactivate Rep" option instead if you want to
              preserve their data while preventing login access.
            </Text>
            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeleteRep}
                loading={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Permanently"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    </RepDetailsShell>
  );
}

export default RepDetailsPage;
